import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, Send, Loader2, Building2, Phone, FileText } from 'lucide-react';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import api, { setToken } from '../api/client';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import GoogleSignInButton from '../components/GoogleSignInButton';
import MicrosoftSignInButton from '../components/MicrosoftSignInButton';
import DemoModeSelector from '../components/DemoModeSelector';
import OrgPicker from '../components/OrgPicker';

function formatApiError(detail) {
  if (!detail) return 'Une erreur est survenue.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' · ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function InputWithIcon({ icon: Icon, testId, ...props }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
      <input
        data-testid={testId}
        {...props}
        className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[14px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
      />
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading, setUser } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  // Vague 113 Wave 1m — demo signup fields
  const [orgName, setOrgName] = useState('');
  const [phone, setPhone] = useState('');
  const [siret, setSiret] = useState('');
  const [objectives, setObjectives] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [magicSent, setMagicSent] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  // Demo signup — chosen seed mode (discovery|blank). null = not chosen yet.
  const [demoSeedMode, setDemoSeedMode] = useState(null);
  // Vague 112 — 2FA challenge state. When /auth/login returns requires_2fa,
  // we hold the challenge id here and render a dedicated code-entry screen
  // instead of the email/password form until the challenge is resolved.
  const [twoFactor, setTwoFactor] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // Vague 114 — multi-org: same email in several sociétés → org picker
  const [orgChoice, setOrgChoice] = useState(null);

  // Vague 113 Wave 1l — adaptive login: when the typed email maps to an org
  // with a custom auth policy, hide forbidden methods + show a hint about a
  // forced SSO method (e.g. "@cabinet.fr → Microsoft only").
  const [effectivePolicy, setEffectivePolicy] = useState({
    allow_password: true, allow_google: true, allow_microsoft: true, forced_method: null,
  });
  useEffect(() => {
    const e = (email || '').trim();
    if (!e || !e.includes('@')) {
      setEffectivePolicy({ allow_password: true, allow_google: true, allow_microsoft: true, forced_method: null });
      return;
    }
    const tid = setTimeout(() => {
      api.get(`/auth-policy/effective-for-email?email=${encodeURIComponent(e)}`)
        .then(({ data }) => setEffectivePolicy(data))
        .catch(() => { /* keep defaults */ });
    }, 350); // debounce while user types
    return () => clearTimeout(tid);
  }, [email]);

  // Super admin → /superadmin ; external trainer → /trainer ; everyone else → /app.
  // Vague 113 L6 — Admins (any flavor) are always routed to /app even if
  // the backend mistakenly tags them as `is_external` (legacy trainers row).
  const _landingFor = (u) => {
    const role = (u?.role || '').toLowerCase().replace(' ', '_');
    if (role === 'super_admin' || role === 'superadmin') return '/superadmin';
    const isAdmin = role === 'admin' || role === 'administrateur' || role === 'owner';
    if (!isAdmin && u?.trainer_profile?.is_external) return '/trainer';
    return '/app';
  };

  useEffect(() => {
    if (!authLoading && user) navigate(_landingFor(user), { replace: true });
  }, [user, authLoading, navigate]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double-submit / race
    setError(null);
    setSubmitting(true);
    try {
      let endpoint, body;
      if (mode === 'login') {
        endpoint = '/auth/login';
        body = { email, password };
      } else if (mode === 'register') {
        // Vague 113 Wave 1m — every signup creates an isolated demo org.
        endpoint = '/auth/register-demo';
        const utm = new URLSearchParams(window.location.search).get('utm_source') || '';
        body = {
          email, password,
          name: name || email.split('@')[0],
          org_name: orgName,
          phone: phone || '',
          siret: siret || '',
          objectives: objectives || '',
          utm_source: utm,
          seed_mode: demoSeedMode || 'discovery',
        };
      } else {
        endpoint = '/auth/register';  // fallback (kept for legacy)
        body = { email, password, name: name || undefined };
      }
      const { data } = await api.post(endpoint, body);
      // 2FA gated login: backend returns a challenge instead of a session.
      if (data.requires_2fa && data.challenge) {
        setTwoFactor({ challenge: data.challenge, expires_in_minutes: data.expires_in_minutes || 5 });
        setTwoFactorCode('');
        return;
      }
      // Multi-org: the email belongs to several organizations → picker
      if (data.requires_org_choice && data.challenge) {
        setOrgChoice(data);
        return;
      }
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      navigate(_landingFor(data.user), { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Connexion impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const submitTwoFactor = async (e) => {
    e.preventDefault();
    if (submitting || !twoFactor) return;
    setError(null);
    setSubmitting(true);
    try {
      const clean = twoFactorCode.replace(/\s|-/g, '');
      const { data } = await api.post('/auth/2fa/verify-login', {
        challenge: twoFactor.challenge, token: clean,
      });
      if (data.session_token) setToken(data.session_token);
      setUser(data.user);
      setTwoFactor(null);
      navigate(_landingFor(data.user), { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || 'Code incorrect');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/magic-link/request', { email });
      setMagicSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next) => { setMode(next); setError(null); setForgotSent(false); };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      <div className="flex-1 flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-10">
        <div className="flex items-center" data-testid="login-brand">
          <Logo size={64} variant="full" />
        </div>

        <div className="flex-1 flex items-center justify-center py-8 lg:py-0">
          <div className="w-full max-w-[420px] mx-auto">
            <h1 className="text-[28px] sm:text-[32px] font-bold text-[#1A1B2E] leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Bonjour, ravi de vous revoir
            </h1>
            <p className="text-[14.5px] text-[#6B6C85] mt-2 mb-6">
              Connectez-vous pour piloter votre activité de formation.
            </p>

            <Tabs defaultValue="email" className="w-full">
              <TabsList data-testid="login-tabs" className="grid grid-cols-2 w-full h-11 bg-[#F6F7FB] rounded-xl p-1">
                <TabsTrigger data-testid="tab-email" value="email" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1B2E] data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg">Email</TabsTrigger>
                <TabsTrigger data-testid="tab-magic" value="magic" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1B2E] data-[state=active]:shadow-sm text-[12.5px] font-semibold rounded-lg">Lien magique</TabsTrigger>
              </TabsList>

              {/* --- Email tab --- */}
              <TabsContent value="email" className="mt-5">
                {orgChoice ? (
                  <OrgPicker
                    choice={orgChoice}
                    onTwoFactor={(d) => {
                      setOrgChoice(null);
                      setTwoFactor({ challenge: d.challenge, expires_in_minutes: d.expires_in_minutes || 5 });
                      setTwoFactorCode('');
                    }}
                    onBack={() => { setOrgChoice(null); setError(null); }}
                  />
                ) : twoFactor ? (
                  <form onSubmit={submitTwoFactor} className="space-y-3" data-testid="two-factor-challenge">
                    <div className="mb-2">
                      <h2 className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Vérification en deux étapes</h2>
                      <p className="text-[12.5px] text-[#6B6C85] mt-0.5">
                        Entrez le code à 6 chiffres affiché par votre application d'authentification.
                      </p>
                    </div>
                    <input
                      autoFocus
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 12))}
                      data-testid="two-factor-login-input"
                      className="w-full h-14 rounded-xl border-2 border-[#E5E7EB] focus:border-[#4F46E5] px-4 text-center tracking-[0.3em] text-[22px] font-bold font-mono"
                      placeholder="000000"
                      maxLength={12}
                      inputMode="numeric"
                    />
                    <p className="text-[11px] text-[#8E8FA5]">
                      Pas accès à votre téléphone ? Saisissez un code de secours (format <span className="font-mono">XXXXX-XXXXX</span>).
                    </p>
                    {error && <div data-testid="login-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>}
                    <button type="submit" disabled={submitting || twoFactorCode.length < 6} data-testid="two-factor-login-submit"
                            className="w-full h-12 rounded-xl brand-gradient text-white font-semibold disabled:opacity-60">
                      {submitting ? 'Vérification…' : 'Valider'}
                    </button>
                    <button type="button" onClick={() => { setTwoFactor(null); setTwoFactorCode(''); setError(null); }}
                            className="w-full text-[12.5px] text-[#6B6C85] hover:text-[#4F46E5] font-semibold">
                      Utiliser un autre compte
                    </button>
                  </form>
                ) : (
                <>
                {/* Header title per mode */}
                <div className="mb-4">
                  <h2 className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {mode === 'login' && 'Connexion à votre compte'}
                    {mode === 'register' && 'Créer un compte'}
                    {mode === 'forgot' && 'Mot de passe oublié'}
                  </h2>
                  <p className="text-[12.5px] text-[#6B6C85] mt-0.5">
                    {mode === 'login' && 'Entrez votre email et votre mot de passe.'}
                    {mode === 'register' && 'Un compte gratuit, créé en 10 secondes.'}
                    {mode === 'forgot' && 'Nous vous enverrons un lien pour définir un nouveau mot de passe.'}
                  </p>
                </div>

                {/* Forgot success card */}
                {mode === 'forgot' && forgotSent ? (
                  <div data-testid="forgot-sent" className="p-4 rounded-xl bg-[#E6FBF7] border border-[#A7F3D0] text-[#0F766E]">
                    <div className="font-semibold text-[14px] mb-1">Email envoyé ✓</div>
                    <p className="text-[12.5px] leading-relaxed">
                      Si un compte correspond à <strong>{email}</strong>, un email vient d'être envoyé avec un lien de réinitialisation. Il expire dans 60 minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="mt-3 text-[12px] font-semibold underline"
                    >
                      Retour à la connexion
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={mode === 'forgot' ? handleForgot : handleEmailSubmit}
                    className="space-y-3"
                  >
                    {mode === 'register' && !demoSeedMode && (
                      <DemoModeSelector onSelect={setDemoSeedMode} />
                    )}
                    {mode === 'register' && demoSeedMode && (
                      <div
                        data-testid="demo-mode-recap"
                        className="flex items-center justify-between text-[12px] bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2"
                      >
                        <span className="text-indigo-900">
                          {demoSeedMode === 'discovery'
                            ? '🚀 Mode Découverte (données pré-remplies)'
                            : '📝 Mode Vierge + Assistant'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setDemoSeedMode(null)}
                          data-testid="demo-mode-change"
                          className="text-indigo-600 hover:text-indigo-900 font-semibold"
                        >
                          Changer
                        </button>
                      </div>
                    )}
                    {mode === 'register' && demoSeedMode && (
                      <></>
                    )}
                    {mode === 'register' && demoSeedMode && (
                      <>
                        <InputWithIcon
                          icon={User}
                          testId="login-name"
                          type="text"
                          placeholder="Votre nom complet *"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          autoComplete="name"
                        />
                        <InputWithIcon
                          icon={Building2}
                          testId="register-org-name"
                          type="text"
                          placeholder="Raison sociale de votre organisme *"
                          required
                          value={orgName}
                          onChange={(e) => setOrgName(e.target.value)}
                          autoComplete="organization"
                        />
                        <InputWithIcon
                          icon={Phone}
                          testId="register-phone"
                          type="tel"
                          placeholder="Téléphone (optionnel)"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          autoComplete="tel"
                        />
                        <InputWithIcon
                          icon={FileText}
                          testId="register-siret"
                          type="text"
                          placeholder="SIRET (optionnel)"
                          value={siret}
                          onChange={(e) => setSiret(e.target.value)}
                          maxLength={14}
                        />
                        <textarea
                          data-testid="register-objectives"
                          rows={2}
                          placeholder="Que cherchez-vous à faire avec OFManager ? (optionnel)"
                          value={objectives}
                          onChange={(e) => setObjectives(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] text-[14px] resize-none focus:outline-none focus:border-[#4F46E5]"
                        />
                      </>
                    )}
                    {(mode !== 'register' || demoSeedMode) && (
                    <InputWithIcon
                      icon={Mail}
                      testId="login-email"
                      type="email"
                      placeholder="adresse@email.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    )}
                    {mode !== 'forgot' && (mode !== 'register' || demoSeedMode) && (
                      <InputWithIcon
                        icon={Lock}
                        testId="login-password"
                        type="password"
                        placeholder={mode === 'register' ? 'Mot de passe (min. 8 caractères)' : 'Mot de passe'}
                        required
                        minLength={mode === 'register' ? 8 : undefined}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                      />
                    )}
                    {mode === 'register' && demoSeedMode && <PasswordStrengthMeter value={password} />}
                    {error && (
                      <div data-testid="login-error" className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
                    )}
                    {(mode !== 'register' || demoSeedMode) && (
                    <button
                      data-testid="login-submit"
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 rounded-xl brand-gradient text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {mode === 'login' && 'Se connecter'}
                      {mode === 'register' && 'Créer mon compte'}
                      {mode === 'forgot' && 'Envoyer le lien de réinitialisation'}
                    </button>
                    )}
                  </form>
                )}

                {/* Secondary links row */}
                <div className="mt-5 flex items-center justify-between gap-2 text-[12.5px]">
                  {mode === 'login' && (
                    <>
                      <button
                        data-testid="link-forgot"
                        type="button"
                        onClick={() => switchMode('forgot')}
                        className="text-[#6B6C85] hover:text-[#4F46E5] transition-colors"
                      >
                        Mot de passe oublié&nbsp;?
                      </button>
                      <button
                        data-testid="link-register"
                        type="button"
                        onClick={() => switchMode('register')}
                        className="text-[#4F46E5] font-semibold hover:underline"
                      >
                        Créer un compte
                      </button>
                    </>
                  )}
                  {(mode === 'register' || mode === 'forgot') && (
                    <button
                      data-testid="link-back-login"
                      type="button"
                      onClick={() => switchMode('login')}
                      className="text-[#4F46E5] font-semibold hover:underline mx-auto"
                    >
                      ← Retour à la connexion
                    </button>
                  )}
                </div>
                </>
                )}
              </TabsContent>

              {/* --- Magic link tab --- */}
              <TabsContent value="magic" className="mt-5">
                {magicSent ? (
                  <div data-testid="magic-sent" className="p-4 rounded-xl bg-[#E6FBF7] border border-[#A7F3D0] text-[#0F766E]">
                    <div className="font-semibold text-[14px] mb-1">Lien envoyé ✓</div>
                    <p className="text-[12.5px] leading-relaxed">
                      Si un compte correspond à <strong>{email}</strong>, un email vient d'être envoyé avec un lien de connexion. Il expire dans 15 minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setMagicSent(false); setEmail(''); }}
                      className="mt-3 text-[12px] font-semibold underline"
                    >
                      Utiliser un autre email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleMagic} className="space-y-3">
                    <p className="text-[12.5px] text-[#6B6C85] leading-relaxed">
                      Recevez un lien de connexion par email — aucun mot de passe à retenir.
                    </p>
                    <InputWithIcon
                      icon={Mail}
                      testId="magic-email"
                      type="email"
                      placeholder="adresse@email.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    {error && (
                      <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
                    )}
                    <button
                      data-testid="magic-submit"
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 rounded-xl brand-gradient text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity disabled:opacity-60"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Envoyer le lien de connexion
                    </button>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#EFF0F6]" />
              <span className="text-[11px] text-[#8E8FA5] uppercase tracking-wider">ou</span>
              <div className="flex-1 h-px bg-[#EFF0F6]" />
            </div>

            {/* Vague 113 Wave 1l — Adaptive SSO buttons based on org auth policy. */}
            {effectivePolicy.forced_method && effectivePolicy.forced_method !== 'password' && (
              <div className="mb-3 p-2.5 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE] text-[#3730A3] text-[12px]" data-testid="forced-method-hint">
                Votre organisation impose la connexion via <strong>{effectivePolicy.forced_method === 'microsoft' ? 'Microsoft' : 'Google'}</strong>. Cliquez sur le bouton ci-dessous.
              </div>
            )}

            {effectivePolicy.allow_google && (
              <GoogleSignInButton
                onError={(msg) => setError(msg || 'Connexion Google impossible')}
              />
            )}

            {effectivePolicy.allow_microsoft && (
              <div className={effectivePolicy.allow_google ? 'mt-3' : ''}>
                <MicrosoftSignInButton
                  onError={(msg) => setError(msg || 'Connexion Microsoft impossible')}
                />
              </div>
            )}

            {!effectivePolicy.allow_password && !effectivePolicy.allow_google && !effectivePolicy.allow_microsoft && (
              <div className="text-[12.5px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2.5" data-testid="no-method-available">
                Aucune méthode de connexion n'est actuellement autorisée pour votre organisation. Contactez votre administrateur.
              </div>
            )}

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#EFF0F6]" />
              <span className="text-[11px] text-[#8E8FA5] uppercase tracking-wider">Sécurisé par OFManager</span>
              <div className="flex-1 h-px bg-[#EFF0F6]" />
            </div>
          </div>
        </div>

        <div className="text-[12px] text-[#8E8FA5]">© 2026 OFManager</div>
      </div>

      {/* Right - Control Deck (Wave 1u9 redesign — distinctive dark premium) */}
      <div
        data-testid="login-right-panel"
        className="hidden lg:flex w-[48%] relative overflow-hidden"
        style={{ background: 'radial-gradient(at 20% 0%, #2A2356 0%, #14132E 55%, #0A0920 100%)' }}
      >
        {/* Grain texture overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        {/* Soft glow accents */}
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(124,92,252,0.35), transparent 65%)' }} />
        <div className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.25), transparent 65%)' }} />
        {/* Faint grid lines */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '56px 56px' }}
        />

        <div className="relative z-10 w-full p-14 flex flex-col justify-between gap-8">
          {/* Top eyebrow + system status */}
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur border border-white/10 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22D3EE] animate-pulse" />
              <span className="text-[11.5px] font-semibold tracking-wide text-white/90 uppercase">OFManager · cockpit</span>
            </div>
          </div>

          {/* Middle — hero copy + live KPIs */}
          <div className="space-y-7 max-w-[480px]">
            <div>
              <h2 className="text-[42px] font-bold text-white leading-[1.05] tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Pilotez chaque formation comme un <span className="bg-gradient-to-r from-[#A5B4FC] to-[#22D3EE] bg-clip-text text-transparent">commandant&nbsp;de&nbsp;bord</span>.
              </h2>
              <p className="text-[15px] text-white/70 mt-3 leading-relaxed">
                Sessions, Qualiopi, devis, conventions, e-mails. Tout au même endroit, sous votre contrôle.
              </p>
            </div>

            <LiveActivityCard />
          </div>

          {/* Bas — un rappel factuel, sans chiffre ni témoignage inventés */}
          <div className="max-w-[480px] text-[12px] text-white/50">
            Logiciel de gestion pour organismes de formation. Chaque organisme dispose de son espace, isolé des autres.
          </div>
        </div>
      </div>
    </div>
  );
}

// Ce que le produit fait — en mots, pas en chiffres. La carte precedente
// affichait « 1 247 conventions generees » et un score Qualiopi qui montaient
// tout seuls toutes les 2,4 secondes : des constantes, incrementees au hasard.
function LiveActivityCard() {
  const points = [
    ["Sessions", "planning, séances, feuilles d'émargement signées au doigt"],
    ['Commercial', 'devis, conventions, signature en ligne'],
    ['Qualiopi', 'indicateurs et pièces rangées par critère'],
    ['Courrier', 'e-mails envoyés depuis votre propre boîte, Microsoft ou Google'],
  ];
  return (
    <div data-testid="login-live-card" className="bg-white/[0.04] backdrop-blur border border-white/10 rounded-2xl p-5 max-w-[480px]">
      <ul className="space-y-3">
        {points.map(([titre, detail]) => (
          <li key={titre} className="flex gap-3">
            <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#A5B4FC] to-[#22D3EE] shrink-0" />
            <div>
              <div className="text-[13px] font-semibold text-white/90">{titre}</div>
              <div className="text-[12px] text-white/55">{detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

